import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { modelKeyAtom } from '../state';

import { type ChatModelInfo, modelsOptions } from '@/_generated/api-clients/xp';

export interface UseChatModelsResult {
  models: ChatModelInfo[];
  /** The currently-selected model, resolved from `modelKeyAtom` or the server default. */
  selected: ChatModelInfo | undefined;
  /** Update the stored selection. */
  selectModel: (key: string) => void;
  isLoading: boolean;
  error: Error | null;
}

export const useChatModels = (): UseChatModelsResult => {
  const { data, isLoading, error } = useQuery(modelsOptions());
  const [modelKey, setModelKey] = useAtom(modelKeyAtom);

  const models = data ?? [];
  const defaultModel = models.find(m => m.isDefault) ?? models[0];

  const stored = modelKey ? models.find(m => m.key === modelKey) : undefined;
  const selected = stored ?? defaultModel;

  useEffect(() => {
    if (!modelKey && defaultModel?.key) {
      setModelKey(defaultModel.key);
    }
  }, [modelKey, defaultModel?.key, setModelKey]);

  return {
    models,
    selected,
    selectModel: setModelKey,
    isLoading,
    error,
  };
};
